import React, { useEffect, useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import { API_ENDPOINTS, apiRequest } from '../config/api';

/**
 * Modal shown before publishing a diagram to GitLab.
 * Fetches entries used in the diagram (already published to GitLab) and lets
 * the user pick one as the "root" for the GitLab path.
 */
const DiagramPublishModal = ({ isOpen, onClose, diagramId, projectId, onConfirm, isPublishing = false, lintDiagnostics = [], fitnessWarnings = [] }) => {
  const { theme } = useTheme();
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasLintDiagnostics = Array.isArray(lintDiagnostics) && lintDiagnostics.length > 0;
  const hasFitnessWarnings = Array.isArray(fitnessWarnings) && fitnessWarnings.length > 0;
  const hasPublishWarnings = hasLintDiagnostics || hasFitnessWarnings;

  useEffect(() => {
    if (!isOpen || !diagramId || !projectId) return;
    setSelected(null);
    setLoading(true);
    apiRequest(API_ENDPOINTS.diagrams.getDiagramEntries(projectId, diagramId))
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('[DiagramPublishModal] failed to load entries', err);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, diagramId, projectId]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selected);
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 9000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const box = {
    background: theme.toolbar?.background || '#fff',
    color: theme.toolbar?.text || '#1e293b',
    border: `1px solid ${theme.toolbar?.border || '#e2e8f0'}`,
    borderRadius: 10,
    padding: '24px 28px',
    minWidth: 380,
    maxWidth: 520,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  };

  const lintBox = {
    border: '1px solid #f59e0b',
    background: theme.name === 'dark' ? 'rgba(120,53,15,0.24)' : '#fffbeb',
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 16,
    maxHeight: 220,
    overflowY: 'auto',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700 }}>
          Опубликовать диаграмму в GitLab
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, opacity: 0.7 }}>
          Выберите корневую систему/запись для сохранения диаграммы.
          {entries.length === 0 && !loading
            ? ' Диаграмма будет опубликована в общую папку diagrams/.'
            : ''}
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: '12px 0', opacity: 0.6 }}>Загрузка...</div>
        )}

        {!loading && entries.length > 0 && (
          <div style={{
            maxHeight: 220, overflowY: 'auto',
            border: `1px solid ${theme.toolbar?.border || '#e2e8f0'}`,
            borderRadius: 6, marginBottom: 16,
          }}>
            {entries.map(entry => {
              const slug = entry.registry_type?.slug || entry.registryType?.slug || '';
              const isSelected = selected?.id === entry.id;
              return (
                <div
                  key={entry.id}
                  onClick={() => setSelected(isSelected ? null : entry)}
                  style={{
                    padding: '9px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: isSelected
                      ? (theme.name === 'dark' ? '#1e3a5f' : '#dbeafe')
                      : 'transparent',
                    borderBottom: `1px solid ${theme.toolbar?.border || '#e2e8f0'}`,
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: isSelected ? '#3b82f6' : (theme.toolbar?.border || '#94a3b8'),
                  }} />
                  <div>
                    <div style={{ fontWeight: isSelected ? 600 : 400, fontSize: 13 }}>
                      {entry.name || entry.code}
                    </div>
                    {slug && (
                      <div style={{ fontSize: 11, opacity: 0.55 }}>
                        {slug} / {entry.code}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selected && (
          <div style={{
            fontSize: 12, opacity: 0.65, marginBottom: 16,
            fontFamily: 'monospace',
          }}>
            Путь: registries/{selected.registry_type?.slug || selected.registryType?.slug}/{selected.code}/diagrams/native/…
          </div>
        )}

        {hasLintDiagnostics && (
          <div style={lintBox}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              Проблемы линтера • {lintDiagnostics.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lintDiagnostics.map((diag, index) => (
                <div key={`${diag.ruleId || 'diag'}-${diag.line || 0}-${index}`} style={{ fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, textTransform: 'uppercase' }}>{diag.severity}</span>
                    {diag.line && <span>стр {diag.line}</span>}
                    {diag.ruleId && <span style={{ opacity: 0.7 }}>{diag.ruleId}</span>}
                  </div>
                  <div>{diag.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasFitnessWarnings && (
          <div style={lintBox}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              Fitness Functions • {fitnessWarnings.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {fitnessWarnings.map((warning, index) => {
                const ruleId = warning.rule_id || warning.ruleId || warning.template_key || warning.function_name;
                return (
                  <div key={`${ruleId || 'fitness'}-${warning.line || 0}-${index}`} style={{ fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, textTransform: 'uppercase' }}>{warning.severity || 'warning'}</span>
                      {warning.line && <span>стр {warning.line}</span>}
                      {ruleId && <span style={{ opacity: 0.7 }}>{ruleId}</span>}
                    </div>
                    <div>{warning.message}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isPublishing}
            style={{
              padding: '7px 18px', borderRadius: 6, cursor: 'pointer',
              background: 'transparent',
              border: `1px solid ${theme.toolbar?.buttonBorder || '#cbd5e1'}`,
              color: theme.toolbar?.buttonText || '#475569',
              fontSize: 13,
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={isPublishing}
            style={{
              padding: '7px 18px', borderRadius: 6, cursor: isPublishing ? 'not-allowed' : 'pointer',
              background: '#3b82f6', border: 'none', color: '#fff',
              fontSize: 13, fontWeight: 600,
              opacity: isPublishing ? 0.6 : 1,
            }}
          >
            {isPublishing ? 'Публикуется...' : hasPublishWarnings ? 'Опубликовать всё равно' : 'Опубликовать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagramPublishModal;
